/** Navigation editor — header/footer/menu links (Phase B).
 *
 * Tabs по локациям. В каждой — list ссылок с inline-edit (title/url/sort)
 * + create form. Drag-drop порядок отложен до Phase B+1; пока меняем
 * вручную через числовое поле sort_order.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Menu as MenuIcon, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { api } from "@/lib/api";

interface NavLink {
  id: number;
  location: string;
  parent_id: number | null;
  title: string;
  url: string;
  icon: string | null;
  badge_text: string | null;
  badge_color: string | null;
  open_in_new_tab: boolean;
  sort_order: number;
  is_visible: boolean;
}

type LocationKind = "flat" | "tree" | "social" | "apps";

interface LocationDef {
  value: string;
  label: string;
  hint: string;
  kind?: LocationKind; // default "flat"
}

const LOCATIONS: LocationDef[] = [
  {
    value: "header_top",
    label: "Header — верхняя строка",
    hint: "Тонкая utility-строка над логотипом: «О компании», «Доставка», «Контакты».",
  },
  {
    value: "header_quick",
    label: "Header — quick-nav",
    hint:
      "Строка под основной шапкой: «Акции», «Новинки», «Распродажа». Если пусто — auto-fallback на топ-категории каталога.",
  },
  {
    value: "footer_column",
    label: "Footer — колонки",
    hint:
      "Дерево колонок: каждый верхнеуровневый item — заголовок колонки (url оставь пустым), его «дети» — ссылки внутри. Можно вкладывать подзаголовки.",
    kind: "tree",
  },
  {
    value: "footer_social",
    label: "Footer — соцсети",
    hint:
      "Иконки соцсетей внизу футера. В поле «иконка» введи slug: vk / telegram / instagram / youtube / whatsapp / rutube / ok / dzen / facebook / twitter.",
    kind: "social",
  },
  {
    value: "footer_apps",
    label: "Footer — приложения",
    hint:
      "Кнопки сторов рядом с QR-кодом. В поле «иконка» введи slug: apple / google / huawei / rustore.",
    kind: "apps",
  },
  {
    value: "footer_legal",
    label: "Footer — legal ссылки",
    hint: "Мелкие легальные ссылки в самом низу (политика, соглашение).",
  },
  {
    value: "mobile_menu",
    label: "Mobile menu",
    hint: "Burger-меню на мобильных. Если пусто — используются ссылки header_top.",
  },
];

export function NavigationPage(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["nav-links"],
    queryFn: async () => {
      const { data } = await api.get<NavLink[]>("/navigation");
      return data;
    },
  });

  const grouped = (data ?? []).reduce<Record<string, NavLink[]>>((acc, l) => {
    (acc[l.location] = acc[l.location] ?? []).push(l);
    return acc;
  }, {});
  for (const arr of Object.values(grouped)) {
    arr.sort((a, b) => a.sort_order - b.sort_order);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div>
        <h1 className="text-2xl font-heading flex items-center gap-2">
          <MenuIcon className="w-6 h-6" />
          Меню Header / Footer
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Управление ссылками в шапке и подвале сайта. Порядок — числовое
          поле <code className="font-mono">sort_order</code> (по возрастанию).
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <Tabs defaultValue="header_top">
          <TabsList className="flex flex-wrap h-auto">
            {LOCATIONS.map((l) => {
              const count = (grouped[l.value] ?? []).length;
              return (
                <TabsTrigger key={l.value} value={l.value} className="text-xs">
                  {l.label}
                  {count > 0 && (
                    <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px]">
                      {count}
                    </Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {LOCATIONS.map((l) => (
            <TabsContent key={l.value} value={l.value} className="space-y-3">
              <p className="text-xs text-muted-foreground">{l.hint}</p>
              {l.kind === "tree" ? (
                <TreeColumnsEditor
                  location={l.value}
                  links={grouped[l.value] ?? []}
                />
              ) : (
                <>
                  <LinksList
                    location={l.value}
                    links={grouped[l.value] ?? []}
                    showIcon={l.kind === "social" || l.kind === "apps"}
                  />
                  <CreateLinkForm location={l.value} />
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function LinksList({
  location,
  links,
  showIcon = false,
}: {
  location: string;
  links: NavLink[];
  showIcon?: boolean;
}) {
  if (links.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          Ссылок пока нет. Добавь первую через форму ниже.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {links.map((link) => (
        <LinkRow
          key={link.id}
          link={link}
          location={location}
          showIcon={showIcon}
        />
      ))}
    </div>
  );
}

function LinkRow({
  link,
  location,
  showIcon = false,
}: {
  link: NavLink;
  location: string;
  showIcon?: boolean;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(link.title);
  const [url, setUrl] = useState(link.url);
  const [sortOrder, setSortOrder] = useState(String(link.sort_order));
  const [badgeText, setBadgeText] = useState(link.badge_text ?? "");
  const [icon, setIcon] = useState(link.icon ?? "");
  const [isVisible, setIsVisible] = useState(link.is_visible);
  const [openInNewTab, setOpenInNewTab] = useState(link.open_in_new_tab);

  const dirty =
    title !== link.title ||
    url !== link.url ||
    Number(sortOrder) !== link.sort_order ||
    (badgeText || null) !== link.badge_text ||
    (icon || null) !== link.icon ||
    isVisible !== link.is_visible ||
    openInNewTab !== link.open_in_new_tab;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${link.id}`, {
        location,
        parent_id: link.parent_id,
        title: title.trim(),
        url: url.trim(),
        icon: icon.trim() || null,
        badge_text: badgeText.trim() || null,
        badge_color: link.badge_color,
        open_in_new_tab: openInNewTab,
        sort_order: Number(sortOrder) || 0,
        is_visible: isVisible,
      });
    },
    onSuccess: () => {
      toast.success("Сохранено");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${link.id}`);
    },
    onSuccess: () => {
      toast.success("Удалено");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: "Удалить ссылку?",
  });

  return (
    <Card className={dirty ? "border-primary/50" : undefined}>
      <CardContent className="p-3 grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 md:col-span-4">
          <Label className="text-xs">Заголовок</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-4">
          <Label className="text-xs">URL</Label>
          <div className="relative">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} />
            {url && (
              <a
                href={url.startsWith("http") ? url : url}
                target="_blank"
                rel="noreferrer"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                title="Открыть"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
        <div className="col-span-4 md:col-span-1">
          <Label className="text-xs">Порядок</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        {showIcon ? (
          <div className="col-span-8 md:col-span-2">
            <Label className="text-xs">Иконка</Label>
            <Input
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="vk / telegram / apple…"
            />
          </div>
        ) : (
          <div className="col-span-8 md:col-span-2">
            <Label className="text-xs">Бейдж</Label>
            <Input
              value={badgeText}
              onChange={(e) => setBadgeText(e.target.value)}
              placeholder="Новое / %"
            />
          </div>
        )}
        <div className="col-span-12 md:col-span-1 flex justify-end gap-1">
          <Button
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "..." : "OK"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => askDelete(true, link.title)}
          >
            <Trash2 className="w-4 h-4 text-error" />
          </Button>
        </div>
        <div className="col-span-12 flex flex-wrap items-center gap-4 pt-1 text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
            />
            Видимо на сайте
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={openInNewTab}
              onChange={(e) => setOpenInNewTab(e.target.checked)}
            />
            Открывать в новой вкладке
          </label>
        </div>
        {deleteDialog}
      </CardContent>
    </Card>
  );
}

function CreateLinkForm({ location }: { location: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [sortOrder, setSortOrder] = useState("100");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location,
        parent_id: null,
        title: title.trim(),
        url: url.trim(),
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: Number(sortOrder) || 0,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success("Создано");
      setTitle("");
      setUrl("");
      setSortOrder("100");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось создать"),
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" />
        Добавить ссылку
      </Button>
    );
  }

  return (
    <Card className="border-primary/40">
      <CardContent className="p-3 grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 md:col-span-5">
          <Label className="text-xs">Заголовок</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Новая ссылка"
          />
        </div>
        <div className="col-span-12 md:col-span-5">
          <Label className="text-xs">URL</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/about или https://…"
          />
        </div>
        <div className="col-span-6 md:col-span-2">
          <Label className="text-xs">Порядок</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        <div className="col-span-12 flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Создать
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tree editor для footer_column ─────────────────────────────────────
// Структура: колонки (parent_id=null, url='') → ссылки/подзаголовки
// (parent_id=<col.id>) → ссылки под подзаголовком (parent_id=<sub.id>).
// Глубина рекурсии: 2 уровня (column → link или sub-heading → links).

function TreeColumnsEditor({
  location,
  links,
}: {
  location: string;
  links: NavLink[];
}) {
  const columns = links
    .filter((l) => l.parent_id === null)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      {columns.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Колонок ещё нет. Создай первую через кнопку ниже.
          </CardContent>
        </Card>
      )}
      {columns.map((col) => (
        <ColumnEditor
          key={col.id}
          column={col}
          allLinks={links}
          location={location}
        />
      ))}
      <CreateColumnButton location={location} />
    </div>
  );
}

function ColumnEditor({
  column,
  allLinks,
  location,
}: {
  column: NavLink;
  allLinks: NavLink[];
  location: string;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(column.title);
  const [sortOrder, setSortOrder] = useState(String(column.sort_order));
  const [isVisible, setIsVisible] = useState(column.is_visible);

  const dirty =
    title !== column.title ||
    Number(sortOrder) !== column.sort_order ||
    isVisible !== column.is_visible;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${column.id}`, {
        location,
        parent_id: null,
        title: title.trim(),
        url: "",
        icon: column.icon,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: Number(sortOrder) || 0,
        is_visible: isVisible,
      });
    },
    onSuccess: () => {
      toast.success("Колонка сохранена");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${column.id}`);
    },
    onSuccess: () => {
      toast.success("Колонка удалена");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: "Удалить колонку?",
    description:
      "Все вложенные ссылки и подзаголовки колонки тоже будут удалены.",
  });

  const children = allLinks
    .filter((l) => l.parent_id === column.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Card className="border-primary/30">
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-12 gap-2 items-end pb-3 border-b">
          <div className="col-span-12 md:col-span-7">
            <Label className="text-xs font-semibold">
              📁 Заголовок колонки
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="font-medium"
            />
          </div>
          <div className="col-span-4 md:col-span-2">
            <Label className="text-xs">Порядок</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>
          <div className="col-span-8 md:col-span-3 flex justify-end gap-1">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer mr-2">
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => setIsVisible(e.target.checked)}
              />
              Видимо
            </label>
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              OK
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => askDelete(true, column.title)}
            >
              <Trash2 className="w-4 h-4 text-error" />
            </Button>
          </div>
        </div>

        {deleteDialog}

        {/* Ссылки и подзаголовки внутри колонки */}
        <div className="space-y-2 pl-4 border-l-2 border-primary/20">
          {children.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Ссылок пока нет
            </p>
          )}
          {children.map((child) => (
            <TreeChildRow
              key={child.id}
              link={child}
              allLinks={allLinks}
              location={location}
            />
          ))}
          <CreateChildButton location={location} parentId={column.id} />
        </div>
      </CardContent>
    </Card>
  );
}

function TreeChildRow({
  link,
  allLinks,
  location,
}: {
  link: NavLink;
  allLinks: NavLink[];
  location: string;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(link.title);
  const [url, setUrl] = useState(link.url);
  const [sortOrder, setSortOrder] = useState(String(link.sort_order));
  const [isVisible, setIsVisible] = useState(link.is_visible);

  const isSubHeading = !link.url;
  const grandchildren = allLinks
    .filter((l) => l.parent_id === link.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  const dirty =
    title !== link.title ||
    url !== link.url ||
    Number(sortOrder) !== link.sort_order ||
    isVisible !== link.is_visible;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${link.id}`, {
        location,
        parent_id: link.parent_id,
        title: title.trim(),
        url: url.trim(),
        icon: link.icon,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: link.open_in_new_tab,
        sort_order: Number(sortOrder) || 0,
        is_visible: isVisible,
      });
    },
    onSuccess: () => {
      toast.success("Сохранено");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${link.id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: isSubHeading ? "Удалить подзаголовок?" : "Удалить ссылку?",
  });

  return (
    <div className="space-y-2">
      <div
        className={`grid grid-cols-12 gap-2 items-end rounded p-2 ${
          isSubHeading ? "bg-primary/5 border border-primary/20" : ""
        }`}
      >
        <div className="col-span-12 md:col-span-4">
          <Label className="text-[10px]">
            {isSubHeading ? "📌 Подзаголовок" : "Заголовок"}
          </Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={isSubHeading ? "font-medium" : ""}
          />
        </div>
        <div className="col-span-9 md:col-span-5">
          <Label className="text-[10px]">URL (пусто = подзаголовок)</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/about"
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Label className="text-[10px]">№</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
          <label className="flex items-center gap-1 text-[10px] cursor-pointer mr-1">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
            />
            ON
          </label>
          <Button
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            OK
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => askDelete(true, link.title)}
          >
            <Trash2 className="w-3.5 h-3.5 text-error" />
          </Button>
        </div>
      </div>

      {deleteDialog}

      {/* Внуки (ссылки под подзаголовком) */}
      {isSubHeading && (
        <div className="ml-6 space-y-2 pl-3 border-l border-border">
          {grandchildren.map((gc) => (
            <TreeChildRow
              key={gc.id}
              link={gc}
              allLinks={allLinks}
              location={location}
            />
          ))}
          <CreateChildButton location={location} parentId={link.id} />
        </div>
      )}
    </div>
  );
}

function CreateColumnButton({ location }: { location: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location,
        parent_id: null,
        title: title.trim(),
        url: "",
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: 100,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success("Колонка создана");
      setTitle("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось создать"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" />
        Добавить колонку
      </Button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новая колонка</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Заголовок колонки</Label>
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: О компании"
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim()) create.mutate();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateChildButton({
  location,
  parentId,
}: {
  location: string;
  parentId: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"link" | "subheading">("link");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location,
        parent_id: parentId,
        title: title.trim(),
        url: kind === "link" ? url.trim() : "",
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: 100,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success(kind === "link" ? "Ссылка добавлена" : "Подзаголовок добавлен");
      setTitle("");
      setUrl("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось создать"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setKind("link");
            setOpen(true);
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Ссылка
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setKind("subheading");
            setOpen(true);
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Подзаголовок
        </Button>
      </div>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {kind === "link" ? "Новая ссылка" : "Новый подзаголовок"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Текст</Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={kind === "link" ? "Реквизиты" : "Партнёрская программа"}
            />
          </div>
          {kind === "link" && (
            <div className="space-y-1.5">
              <Label className="text-xs">URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/about или https://…"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
